import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import { calculateInvoiceSummaryData, DocumentSummaryData, generateInvoiceNoteData, generateReportTotal, ReportTotalDetail } from "@utils/DocumentStatus";
import ReportPage, { ReportDataProps } from "@components/dynamic/ReportPage";
import moment from "moment";
import cx from "classnames";
import styles from "./style.module.scss";
import { toNumberFormat } from "@utils/Utils";
import { CURRENCIES_CODES } from "@utils/Constants";
import LoadingScreen from "@components/content/LoadingScreen";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { isValidEDocument } from "@utils/EDocument";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { SettlementApi } from "@/api/SettlementApi";
import type { SettlementDataType } from "@/api/SettlementApi";
import {
  createProductLinesIssues,
  DOCUMENT_KIND_CREDITNOTE,
  DOCUMENT_KIND_DEBITNOTE,
  DOCUMENT_KIND_INVOICE,
  logProductLinesIssuesSummary,
  ProductLinesIssues,
  sanitizeProductLines
} from "@utils/ReportProductLines";

type SellByPeriodReportPrintProps = {
  excelExport?: boolean
}

type SeparatedTotals = {
  taxable: DocumentSummaryData,
  nonTaxable: DocumentSummaryData

}

/** Totales para las columnas opcionales del Excel (detallar IVA). */
type IvaReportColumnTotals = {
  gravadosTotal: number;
  exentosYExoneradosTotal: number;
  exemptionTaxTotal: number;
  hasExemption: boolean;
};

const SELL_REPORT_COL_TOTAL_GRAVADOS = "Total Productos Gravados";
const SELL_REPORT_COL_TOTAL_EXENTOS = "Total Productos Exentos y exonerados";
const SELL_REPORT_COL_EXONERACION = "Exoneración";

/** Alinea filas NC/ND (13) o factura (14) al ancho del reporte con o sin columnas de detalle IVA. */
const padSellPeriodRowValues = (values: string[], useIvaColumns: boolean): string[] => {
  if (useIvaColumns) {
    if (values.length === 17) {
      return values;
    }
    if (values.length === 14) {
      return [...values.slice(0, 9), "", "", "", ...values.slice(9)];
    }
    if (values.length === 13) {
      return [...values.slice(0, 9), "", "", "", ...values.slice(9), ""];
    }
    return values;
  }
  if (values.length === 14) {
    return values;
  }
  if (values.length === 13) {
    return [...values, ""];
  }
  return values;
};

const SellByPeriodReportPrint = (props: SellByPeriodReportPrintProps) => {
  const { excelExport = false } = props
  const params = useParams()
  const { clientId, zoneId, startDate = "", endDate = "", detallarIva, autoPrint = "0", includeSettlements } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTotals, setReportTotals] = useState<ReportTotalDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
      loadReportData()
    }
  }, [startDate, endDate, excelExport, detallarIva, includeSettlements]);

  const sustractSeparatedTotal = (document: InvoiceDataType | CreditnoteDataType | DebitnoteDataType, products: EDocumentProductDataType[], totals: Record<string, SeparatedTotals> = {}): Record<string, SeparatedTotals> => {
    return sumSeparatedTotal(document, products, totals, -1)
  }
  const sumSeparatedTotal = (document: InvoiceDataType | CreditnoteDataType | DebitnoteDataType, products: EDocumentProductDataType[], totals: Record<string, SeparatedTotals> = {}, factor: 1 | -1 = 1): Record<string, SeparatedTotals> => {
    let currencyTotalas = totals[`${document.currencyId}`] || {
      taxable: {
        subtotal: Number(0),
        descuento: Number(0),
        impuestos: Number(0),
        total: Number(0),
      },
      nonTaxable: {
        subtotal: Number(0),
        descuento: Number(0),
        impuestos: Number(0),
        total: Number(0),
      }
    }
    if (isValidEDocument(`${document.edocument_status_payload}`)) {
      currencyTotalas = products?.reduce((a, p) => {
        let totals;
        if (p.exento) {
          totals = a.nonTaxable
        } else {
          totals = a.taxable
        }

        let taxes = Number(p.impuestos || 0)
        if (p.exemption) {
          try {
            const exemption: ProductExemptionDataType = JSON.parse(`${p.exemption}`);
            taxes -= (factor * Number(exemption?.monto_impuesto || 0))
          } catch (err) {
            console.log("Not able to parce exoneration", err)
          }
        }

        totals.subtotal += (factor * Number(p.subtotal || 0))
        totals.descuento += (factor * Number(p.descuento || 0))
        totals.impuestos += (factor * Number(taxes || 0))
        const total = Number(p.subtotal || 0) - Number(p.descuento || 0) + Number(taxes || 0)
        totals.total += (factor * total)

        return a
      }, currencyTotalas)
    }

    return {
      ...totals,
      [`${document.currencyId}`]: currencyTotalas
    }
  }

  const isProductExempt = (product: EDocumentProductDataType): boolean => {
    if (product.exento) {
      return true;
    }
    const taxRate = Number(product.porcentaje_impuesto || 0);
    return !isNaN(taxRate) && taxRate === 0;
  };

  const getExemptionMontoImpuesto = (product: EDocumentProductDataType): number => {
    if (!product.exemption) {
      return 0;
    }
    try {
      const raw = typeof product.exemption === "string"
        ? JSON.parse(product.exemption)
        : product.exemption;
      const m = Number((raw as ProductExemptionDataType)?.monto_impuesto || 0);
      return isNaN(m) ? 0 : m;
    } catch {
      return 0;
    }
  };

  /** Línea con exoneración (payload parseable), aunque el monto sea 0. */
  const hasProductExoneracion = (product: EDocumentProductDataType): boolean => {
    if (!product.exemption) {
      return false;
    }
    try {
      const raw = typeof product.exemption === "string"
        ? JSON.parse(product.exemption)
        : product.exemption;
      return raw != null && typeof raw === "object";
    } catch {
      return false;
    }
  };

  /**
   * Gravados: solo líneas no exentas y sin exoneración.
   * Exentos (columna): suma del total de líneas exentas + total de líneas con exoneración.
   * Exoneración (columna): suma de monto_impuesto de exoneraciones.
   */
  const computeIvaReportColumnTotals = (products: EDocumentProductDataType[]): IvaReportColumnTotals => {
    let gravadosTotal = 0;
    let exentosYExoneradosTotal = 0;
    let exemptionTaxTotal = 0;

    for (const product of products) {
      const exemptionMonto = getExemptionMontoImpuesto(product);
      exemptionTaxTotal += exemptionMonto;

      const taxesNet = Number(product.impuestos || 0) - exemptionMonto;
      const subtotal = Number(product.subtotal || 0);
      const descuento = Number(product.descuento || 0);
      const lineTotal = subtotal - descuento + taxesNet;

      const exempt = isProductExempt(product);
      const exonerado = hasProductExoneracion(product);

      if (exempt || exonerado) {
        exentosYExoneradosTotal += lineTotal;
      } else {
        gravadosTotal += lineTotal;
      }
    }

    return {
      gravadosTotal,
      exentosYExoneradosTotal,
      exemptionTaxTotal,
      hasExemption: exemptionTaxTotal > 0,
    };
  };

  const enrichNoteEntryWithIvaColumns = (
    baseRow: ReportDataProps,
    products: EDocumentProductDataType[] | undefined,
    useIvaColumns: boolean,
    notePrefix: "NC" | "ND"
  ): ReportDataProps => {
    if (!useIvaColumns) {
      return { ...baseRow, values: padSellPeriodRowValues(baseRow.values, false) };
    }
    const amountPrefix = notePrefix === "NC" ? "-" : "";
    let ivaDetailCols: string[];
    if (products && products.length > 0) {
      const totals = computeIvaReportColumnTotals(products);
      ivaDetailCols = [
        `${amountPrefix}${toNumberFormat(Number(totals.gravadosTotal || 0))}`,
        `${amountPrefix}${toNumberFormat(Number(totals.exentosYExoneradosTotal || 0))}`,
        totals.hasExemption && totals.exemptionTaxTotal
          ? `${amountPrefix}${toNumberFormat(Number(totals.exemptionTaxTotal))}`
          : "",
      ];
    } else {
      ivaDetailCols = ["", "", ""];
    }
    const v = baseRow.values;
    return {
      ...baseRow,
      values: [...v.slice(0, 9), ...ivaDetailCols, ...v.slice(9), ""],
    };
  };

  const renderTotalEntry = (summary: DocumentSummaryData, currencyCode: string): ReportTotalDetail[] => {
    return [
      {
        title: `Subtotal`,
        total: summary.subtotal,
        currencyCode: currencyCode
      },
      {
        title: `Descuento`,
        total: summary.descuento,
        currencyCode: currencyCode
      },
      {
        title: `Impuestos`,
        total: summary.impuestos,
        currencyCode: currencyCode
      },
      {
        title: `Total`,
        total: summary.total,
        currencyCode: currencyCode
      }
    ]

  }

  const addSettlementToSeparatedTotals = (settlement: SettlementDataType, totals: Record<string, SeparatedTotals>): Record<string, SeparatedTotals> => {
    if (!!settlement.anulada) return totals;
    const key = `${settlement.currencyId}`;
    const current = totals[key] || {
      taxable: { subtotal: 0, descuento: 0, impuestos: 0, total: 0 },
      nonTaxable: { subtotal: 0, descuento: 0, impuestos: 0, total: 0 },
    };
    current.taxable.subtotal += Number(settlement.subtotal || 0);
    current.taxable.descuento += Number(settlement.descuento || 0);
    current.taxable.impuestos += Number(settlement.impuestos || 0);
    current.taxable.total += Number(settlement.total || 0);
    return { ...totals, [key]: current };
  };

  const generateSeparatedTotalLines = (separatedTotals: Record<string, SeparatedTotals>, currencies: CurrencyDataType[]): ReportTotalDetail[] => {
    const totals = Object.entries(separatedTotals).flatMap(([key, value]) => {
      const currency = currencies.find(c => c.id == key);
      const currencyId = currency?.currency_id || 1

      return [
        {
          title: `Detalle Venta en ${CURRENCIES_CODES[currencyId]}`
        },
        {
          title: `separator`,
          separatorLine: true
        },
        {
          title: `Total Venta Gravada en ${CURRENCIES_CODES[currencyId]}`
        },
        ...(renderTotalEntry(value.taxable, CURRENCIES_CODES[currencyId])),
        { title: "separator", separatorLine: true },
        {
          title: `Total Venta Exenta en ${CURRENCIES_CODES[currencyId]}`
        },
        ...(renderTotalEntry(value.nonTaxable, CURRENCIES_CODES[currencyId])),
        { title: "separator", separatorLine: true },
      ]
    })
    return totals
  }

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      let filters = {}
      if (clientId && clientId != "-2") {// -2 all the propviders
        filters = {
          clientId: { eq: clientId }
        }
      }
      if (zoneId && zoneId != "-2") {// -2 all the propviders
        filters = {
          ...filters,
          client: { zoneId: { eq: zoneId } }
        }
      }

      const { items: currencies } = await CurrencyApi.fetchAll();
      const { items: invoices } = await InvoiceApi.fetchAllByPeriod(startDate, endDate, filters, "", 10000)
      const { items: creditnotes } = await CreditnoteApi.fetchAllByPeriod(startDate, endDate, filters, "", 10000)
      const { items: debitnotes } = await DebitnoteApi.fetchAllByPeriod(startDate, endDate, filters, "", 10000)

      let settlements: SettlementDataType[] = [];
      if (includeSettlements === "1" && clientId && clientId !== "-2") {
        const { items: settlementItems } = await SettlementApi.fetchAllByClientDate(clientId, endDate, filters, "", 10000, "ASC", startDate);
        settlements = settlementItems.filter((s) => s.fecha && s.fecha >= startDate);
      }

      let separatedTotals: Record<string, SeparatedTotals> = {}

      const currenciesOnReport = new Set<string>();
      const productLinesIssues: ProductLinesIssues = createProductLinesIssues();

      const filterCreditnote = creditnotes.filter(c => c.invoice?.fecha < startDate)
      const filterDebitnote = debitnotes.filter(c => c.invoice?.fecha < startDate)

      const useIvaColumns = excelExport && detallarIva === "1";

      const isNoteInReportPeriod = (note: { created?: string | null }): boolean => {
        const created = `${note?.created || ""}`;
        if (!created) return false;
        return created >= startDate && created < endDate;
      };

      const invoiceBlocks: ReportDataProps[][] = invoices.map(d => {
        const anulada = !!d.anulada
        const isRejected = !isValidEDocument(`${d.edocument_status_payload}`)
        const excludeDocumentFromTotal = anulada || isRejected;
        const invoiceProducts = sanitizeProductLines(d.invoiceProducts as EDocumentProductDataType[], d, DOCUMENT_KIND_INVOICE, productLinesIssues);
        if (!excludeDocumentFromTotal) {
          separatedTotals = sumSeparatedTotal(d, invoiceProducts, separatedTotals)
          for (const cn of (d.creditnotes || []).filter(isNoteInReportPeriod)) {
            separatedTotals = sustractSeparatedTotal(cn, sanitizeProductLines(cn.creditnoteProducts as EDocumentProductDataType[], cn, DOCUMENT_KIND_CREDITNOTE, productLinesIssues), separatedTotals)
          }
          for (const dn of (d.debitnotes || []).filter(isNoteInReportPeriod)) {
            separatedTotals = sumSeparatedTotal(dn, sanitizeProductLines(dn.debitnoteProducts as EDocumentProductDataType[], dn, DOCUMENT_KIND_DEBITNOTE, productLinesIssues), separatedTotals)
          }
        }

        const date = moment(d.fecha, "YYYY-MM-DD")
        const formatDate = date.format("DD-MM-YYYY")
        const currencyId = Number(d.currency.currency_id || 1);
        const { totalCreditNotes = 0, totalDebitNotes = 0 } = calculateInvoiceSummaryData(d, startDate, endDate);
        currenciesOnReport.add(CURRENCIES_CODES[currencyId]);

        const invoiceValuesHead: string[] = [
          `${d.numero}`,
          formatDate,
          `${d.client?.nombre || d.nombre_factura}`,
          anulada ? "Si" : "No",
          isRejected ? "Si" : "No",
          `${toNumberFormat(Number(d.subtotal || 0))}`,
          `${toNumberFormat(Number(d.descuento || 0))}`,
          `${toNumberFormat(Number(d.impuestos || 0))}`,
          `${toNumberFormat(Number(d.total || 0))}`,
        ];

        let ivaDetailCols: string[] = [];
        if (useIvaColumns) {
          if (invoiceProducts.length > 0) {
            const totals = computeIvaReportColumnTotals(invoiceProducts);
            ivaDetailCols = [
              `${toNumberFormat(Number(totals.gravadosTotal || 0))}`,
              `${toNumberFormat(Number(totals.exentosYExoneradosTotal || 0))}`,
              totals.hasExemption && totals.exemptionTaxTotal
                ? `${toNumberFormat(Number(totals.exemptionTaxTotal))}`
                : "",
            ];
          } else {
            ivaDetailCols = ["", "", ""];
          }
        }

        const invoiceValuesTail: string[] = [
          CURRENCIES_CODES[currencyId],
          `${d.exchange_rate}`,
          `${toNumberFormat(totalCreditNotes)}`,
          `${toNumberFormat(totalDebitNotes)}`,
          `${d.reference_number || ""}`,
        ];

        const invoiceValues = useIvaColumns
          ? [...invoiceValuesHead, ...ivaDetailCols, ...invoiceValuesTail]
          : [...invoiceValuesHead, ...invoiceValuesTail];

        let data: ReportDataProps<InvoiceDataType> = {
          sourceData: d,
          excludeFromTotal: excludeDocumentFromTotal,
          className: cx({
            [styles.anulada]: anulada
          }),
          values: invoiceValues
        }

        const validCreditnotes = (d.creditnotes || []).filter(
          (n) => isValidEDocument(`${n.edocument_status_payload}`) && isNoteInReportPeriod(n)
        );
        const creditnoteEntries = validCreditnotes.map((cn) =>
          enrichNoteEntryWithIvaColumns(
            generateInvoiceNoteData(d, [cn], "NC", excludeDocumentFromTotal)[0],
            sanitizeProductLines(cn.creditnoteProducts as EDocumentProductDataType[], cn, DOCUMENT_KIND_CREDITNOTE, productLinesIssues),
            useIvaColumns,
            "NC"
          )
        );
        const validDebitnotes = (d.debitnotes || []).filter(
          (n) => isValidEDocument(`${n.edocument_status_payload}`) && isNoteInReportPeriod(n)
        );
        const debitnoteEntries = validDebitnotes.map((dn) =>
          enrichNoteEntryWithIvaColumns(
            generateInvoiceNoteData(d, [dn], "ND", excludeDocumentFromTotal)[0],
            sanitizeProductLines(dn.debitnoteProducts as EDocumentProductDataType[], dn, DOCUMENT_KIND_DEBITNOTE, productLinesIssues),
            useIvaColumns,
            "ND"
          )
        );

        return [
          data,
          ...creditnoteEntries,
          ...debitnoteEntries
        ]
      });

      settlements.forEach((s) => {
        separatedTotals = addSettlementToSeparatedTotals(s, separatedTotals);
        if (s.currency?.currency_id) {
          currenciesOnReport.add(CURRENCIES_CODES[Number(s.currency.currency_id) || 1]);
        }
      });

      const settlementBlocks: ReportDataProps[][] = settlements
        .filter((s) => !s.anulada)
        .map((s) => {
          const date = moment(s.fecha, "YYYY-MM-DD");
          const formatDate = date.format("DD-MM-YYYY");
          const currencyId = Number(s.currency?.currency_id || 1);
          const settlementHead: string[] = [
            `L-${s.numero}`,
            formatDate,
            `${s.client?.nombre || s.nombre_factura || ""}`,
            "No",
            "No",
            `${toNumberFormat(Number(s.subtotal || 0))}`,
            `${toNumberFormat(Number(s.descuento || 0))}`,
            `${toNumberFormat(Number(s.impuestos || 0))}`,
            `${toNumberFormat(Number(s.total || 0))}`,
          ];
          const settlementTail: string[] = [
            CURRENCIES_CODES[currencyId],
            `${s.exchange_rate ?? ""}`,
            "",
            "",
            `${s.reference_number || ""}`,
          ];
          const settlementValues = useIvaColumns
            ? [...settlementHead, "", "", "", ...settlementTail]
            : [...settlementHead, ...settlementTail];
          return [{
            values: settlementValues
          }];
        });

      const sortByDate = (a: ReportDataProps[], b: ReportDataProps[]) => {
        const dateA = a[0]?.values?.[1] ? moment(a[0].values[1] as string, "DD-MM-YYYY").valueOf() : 0;
        const dateB = b[0]?.values?.[1] ? moment(b[0].values[1] as string, "DD-MM-YYYY").valueOf() : 0;
        return dateA - dateB;
      };
      const allBlocks = [...invoiceBlocks, ...settlementBlocks].sort(sortByDate);
      let reportData: ReportDataProps[] = allBlocks.flat();

      if (filterCreditnote.length > 0 || filterDebitnote.length > 0) {

        if (filterCreditnote && filterCreditnote.length > 0) {
          for (const cn of filterCreditnote) {
            separatedTotals = sustractSeparatedTotal(cn, sanitizeProductLines(cn.creditnoteProducts as EDocumentProductDataType[], cn, DOCUMENT_KIND_CREDITNOTE, productLinesIssues), separatedTotals)
          }
        }

        if (filterDebitnote && filterDebitnote.length > 0) {
          for (const dn of filterDebitnote) {
            separatedTotals = sumSeparatedTotal(dn, sanitizeProductLines(dn.debitnoteProducts as EDocumentProductDataType[], dn, DOCUMENT_KIND_DEBITNOTE, productLinesIssues), separatedTotals)
          }
        }

        const emptyRow = (): string[] =>
          useIvaColumns
            ? ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
            : ["", "", "", "", "", "", "", "", "", "", "", "", ""];
        reportData.push({ values: emptyRow() })
        reportData.push({
          values: (() => {
            const row = emptyRow();
            row[2] = "Documentos Adicionales de este mes";
            return row;
          })()
        })

        filterCreditnote.forEach((cn: CreditnoteDataType) => {
          if (!cn.invoice || !isValidEDocument(`${cn.edocument_status_payload}`)) {
            return;
          }
          reportData.push(
            enrichNoteEntryWithIvaColumns(
              generateInvoiceNoteData(cn.invoice, [cn], "NC")[0],
              sanitizeProductLines(cn.creditnoteProducts as EDocumentProductDataType[], cn, DOCUMENT_KIND_CREDITNOTE, productLinesIssues),
              useIvaColumns,
              "NC"
            )
          );
        });
        filterDebitnote.forEach((dn) => {
          if (!dn.invoice || !isValidEDocument(`${dn.edocument_status_payload}`)) {
            return;
          }
          reportData.push(
            enrichNoteEntryWithIvaColumns(
              generateInvoiceNoteData(dn.invoice, [dn], "ND")[0],
              sanitizeProductLines(dn.debitnoteProducts as EDocumentProductDataType[], dn, DOCUMENT_KIND_DEBITNOTE, productLinesIssues),
              useIvaColumns,
              "ND"
            )
          );
        });
      }

      const separatedTotalData = generateSeparatedTotalLines(separatedTotals, currencies)
      const reportTotal = generateReportTotal(reportData, [
        {
          dataColumnIndex: 5,
          title: "Subtotal Compras"
        },
        {
          dataColumnIndex: 6,
          title: "Total Descuento"
        },
        {
          dataColumnIndex: 7,
          title: "Total IVA"
        },
        {
          dataColumnIndex: 8,
          title: "Total Compras"
        },
      ], currenciesOnReport, useIvaColumns ? 12 : 9);

      logProductLinesIssuesSummary(productLinesIssues, "Reporte Ventas")

      setReportData(reportData)
      setReportTotals([
        ...separatedTotalData,
        ...reportTotal
      ])
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const useIvaDetailColumns = excelExport && detallarIva === "1";

  const reportTitles = useMemo(() => {
    const titlesThroughTotal = [
      { label: "# Factura", width: "10%" },
      { label: "Fecha", width: "10%" },
      { label: "Cliente", width: "25%" },
      { label: "Anulada", width: "5%" },
      { label: "Rechazada", width: "5%" },
      { label: "Subtotal", width: "10%", isNumeric: true },
      { label: "Descuento", width: "10%", isNumeric: true },
      { label: "Iva", width: "10%", isNumeric: true },
      { label: "Total", width: "10%", isNumeric: true },
    ];
    const ivaDetailTitles = useIvaDetailColumns
      ? [
        { label: SELL_REPORT_COL_TOTAL_GRAVADOS, width: "10%", isNumeric: true, hideFromPdf: true },
        { label: SELL_REPORT_COL_TOTAL_EXENTOS, width: "10%", isNumeric: true, hideFromPdf: true },
        { label: SELL_REPORT_COL_EXONERACION, width: "10%", isNumeric: true, hideFromPdf: true },
      ]
      : [];
    const titlesTail = [
      { label: "Moneda", width: "5%" },
      { label: "Tipo de Cambio", width: "5%", isNumeric: true },
      { label: "Total Notas Credito", width: "0%", hideFromPdf: true, isNumeric: true },
      { label: "Total Notas Debito", width: "0%", hideFromPdf: true, isNumeric: true },
      { label: `${suscription.reference_label || "# Referencia"}`, width: "0%", hideFromPdf: true },
    ];
    return [...titlesThroughTotal, ...ivaDetailTitles, ...titlesTail];
  }, [suscription.reference_label, useIvaDetailColumns]);

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={2000}
      >
        <ReportPage
          excelExport={excelExport}
          loading={isLoading}
          suscription={suscription}
          reportConfig={{
            title: "Reporte Ventas",
            date: `${moment().format()}`,
          }}
          reportTotals={reportTotals}
          reportData={reportData}
          reportTitles={reportTitles}
        />
      </PrintPage>
      <LoadingScreen isLoading={isLoading} />

    </div>
  );
}

export default SellByPeriodReportPrint;
